import * as React from 'react';
import * as UI from 'core/ui';
import * as Images from 'pos/Images';
import { Res, TicketDTO, TicketLineDTO } from 'core/api';
import * as Division from './Division';
import { SessionProvider } from 'core/infrastructure';
import { ActionButtonDefinition } from 'pos/ui';
import { Col, Cols, ErrorBlock, Flex } from 'core/ui';
import { getDivisionParts } from './getDivisionParts';
import { DivisionScreen } from './DivisionScreen';
import { Modal } from 'core/ui-next/modal';
import { TicketControl } from 'pos/ui/TicketControl';
import { Box } from '@chakra-ui/react';

type Props = { initialStatus: Division.TicketDivisionStatus, tableCompanions: Nullable<number> } & Modal.Actions<void>;
type State = Division.TicketDivisionStatus & {
	selectedLine?: TicketLineDTO | null;
	selectedSide?: 'Left' | 'Right' | null;
};
type SplitTicketControlProps = {
	ticket: TicketDTO;
	ticketRole: 'Left' | 'Right';
	title: string;
	selectedLine?: Nullable<TicketLineDTO>;
	onSelect: (line: TicketLineDTO | null) => void;
};
interface TicketControlRef {
	getSelectedLineId: () => number | undefined;
	selectLineId: (id: number) => void;
}

const SplitTicketControl = React.forwardRef<TicketControlRef, SplitTicketControlProps>((props, ref) => {

	const { ticket, selectedLine, onSelect, title } = props;
	const [selectedLineId, setSelectedLineId] = React.useState(selectedLine?.Id);
	const hasCashDiscount = props.ticket.CashDiscount !== 0;
	const hasPercentageDiscount = props.ticket.DiscountPercentage !== 0;
	const hasCustomer = props.ticket.Customer.Id !== 0;

	React.useImperativeHandle(ref, () => ({
		getSelectedLineId: () => selectedLineId,
		selectLineId: (id: number) => setSelectedLineId(id)
	}));

	React.useEffect(() => {
		setSelectedLineId(selectedLine?.Id);
	}, [selectedLine]);

	return <Flex.Vertical>
		<Flex.Item className='text-26 text-uppercase text-center p-xs'>
			{title}
		</Flex.Item>
		<Flex.Item size={'4ex'} className={`text-16 font-bold p-xs ${props.ticketRole === 'Left' ? 'text-right' : 'text-left'}`}>
			{ticket.TableCompanions ? Res.Web.SPLIT_TICKET_TABLE_COMPANIONS_LBL(ticket.TableCompanions) : <span />}
		</Flex.Item>
		<Flex.Item size='*' style={{ padding: '0 8px' }}>
			<TicketControl
				ticket={ticket}
				selectedLine={ticket.Lines.find(line => line.Id === selectedLineId) ?? null}
				onSelect={line => { setSelectedLineId(line?.Id); onSelect(line); }}
				showTicketStatus={false}
				expandedTicket={false}
			/>
		</Flex.Item>
		<Flex.Item className='p-xs'>
			{
				hasCustomer
					? <div className='text-right m-t-xs'>{ticket.Customer.FiscalName}</div>
					: null
			}
			{
				hasPercentageDiscount || hasCashDiscount
					? <div className='text-right m-t-xs'>
						<span className='font-bold'>{Res.Web.SPLIT_TICKET_DISCOUNT_LBL()} </span>
						{hasPercentageDiscount ? `${UI.Format.asPercentage(ticket.DiscountPercentage)} (${UI.Format.asMoney(ticket.DiscountPercentageInCash)})` : ''}
						{hasCashDiscount ? UI.Format.asMoney(ticket.CashDiscount) : ''}
					</div>
					: null
			}
			<div className='text-right font-bold text-26'>
				<span className='font-bold'>{Res.Web.SPLIT_TICKET_TOTAL_LBL(ticket.NetAmount)}</span>
			</div>
		</Flex.Item>
	</Flex.Vertical>;
});

export default class WebPosDivisionScreen extends DivisionScreen<State> {

	private leftLines = React.createRef<TicketControlRef>();
	private rightLines = React.createRef<TicketControlRef>();

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {
		const closeButton = SessionProvider.getCurrent().AllowCashTransactions
			? { title: Res.Web.CLOSE_TICKET_BTN(), image: Images.CloseTicket, action: async () => this.setState(await Division.closeTicket(this.state)) }
			: null;

		return [
			this.cancelButton(),
			null,
			null,
			{ title: Res.Web.PRINT_PARK_TICKET_BTN(), image: Images.PrintTicket, action: async () => this.setState(await Division.print(this.state)) },
			closeButton,
			{ title: Res.Web.PRINT_MOVE_TICKET_BTN(), image: Images.MoveTicket, action: async () => this.setState(await Division.move(this.state)) }
		];
	}

	protected getContent(): React.ReactNode {
		const onMoveOneToLeft = async () => {
			let ticketDivision = this.state;
			let selectedLineIndex = this.state.division.Right.Lines.findIndex(x => x.Id === this.state.selectedLine?.Id);
			if (selectedLineIndex !== -1) {
				ticketDivision = await Division.moveOneToLeft(this.state, this.state.division.Right.Lines[selectedLineIndex]);
			}
			// Al mover una línea por completo hacia el otro lado, si era la última línea seleccionada, se queda seleccionada una línea que ya no existe, 
			// por lo que selecciono la nueva última línea del lado original
			if (selectedLineIndex === ticketDivision.division.Right.Lines.length) {
				selectedLineIndex = ticketDivision.division.Right.Lines.length - 1;
			}
			const selectedLine = ticketDivision.division.Right.Lines[selectedLineIndex];

			this.setState({ ...ticketDivision, selectedLine: selectedLine });
		};

		const onMoveOneToRight = async () => {
			let ticketDivision = this.state;
			let selectedLineIndex = this.state.division.Left.Lines.findIndex(x => x.Id === this.leftLines.current?.getSelectedLineId());
			if (selectedLineIndex !== -1) {
				ticketDivision = await Division.moveOneToRight(this.state, this.state.division.Left.Lines[selectedLineIndex]);
			}
			// Al mover una línea por completo hacia el otro lado, si era la última línea seleccionada, se queda seleccionada una línea que ya no existe, 
			// por lo que selecciono la nueva última línea del lado original
			if (selectedLineIndex === ticketDivision.division.Left.Lines.length) {
				selectedLineIndex = ticketDivision.division.Left.Lines.length - 1;
			}
			const selectedLine = ticketDivision.division.Left.Lines[selectedLineIndex];

			this.setState({ ...ticketDivision, selectedLine: selectedLine });

		};

		const onMovePartToRight = async () => {
			const line = this.state.division.Left.Lines.find(x => x.Id === this.leftLines.current?.getSelectedLineId());
			if (line) {
				const result = await getDivisionParts(this.props.tableCompanions);
				if (!result.ok)
					return;

				this.setState(await Division.moveOnePartToRight(this.state, line, result.value), () => this.rightLines.current?.selectLineId(line.Id));
			}
		};

		const onMoveOneTableCompanionsToRight = async () => this.setState(await Division.moveOneTableCompanionsToRight(this.state));
		const onMoveOneTableCompanionsToLeft = async () => this.setState(await Division.moveOneTableCompanionsToLeft(this.state));

		const hasTableCompanions = this.state.division.Left.TableCompanions || this.state.division.Right.TableCompanions;

		return <Box bg={{ base: '{colors.neutral.100}', _dark: '{colors.neutral.900}' }} height={'100%'}>
			<Flex.Vertical>
				<Flex.Item size='*'>
					<Flex.Horizontal>
						<Flex.Item size='*'>
							<SplitTicketControl
								ref={this.leftLines}
								ticket={this.state.division.Left}
								ticketRole='Left'
								title={Res.Web.SPLIT_PENDING_TICKET_LBL()}
								selectedLine={this.state.selectedSide === 'Left' ? this.state.selectedLine : null}
								onSelect={line => this.setState({ selectedLine: line, selectedSide: 'Left' })}
							/>
						</Flex.Item>
						<Flex.Item className='m'>
							<Flex.Vertical>
								{
									hasTableCompanions
										? <Flex.Item className='m-t-xl'>
											<UI.Button style={{ height: '40px', width: '40px', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} className='m-r' onClick={onMoveOneTableCompanionsToLeft}>
												<Images.Left width={28} height={28} />
											</UI.Button>
											<UI.Button style={{ height: '40px', width: '40px', padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} onClick={onMoveOneTableCompanionsToRight}>
												<Images.Right width={28} height={28} />
											</UI.Button>
										</Flex.Item>
										: null
								}
								{
									this.state.mode === 'manual'
										? <Flex.Item size='*' style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
											<UI.Button style={{ height: 65, width: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onMoveOneToRight}><Images.Right /></UI.Button>
											<UI.Button className='m-t m-b' style={{ height: 65, width: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onMovePartToRight}><Images.SplitLinePart /></UI.Button>
											<UI.Button style={{ height: 65, width: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onMoveOneToLeft}><Images.Left /></UI.Button>
										</Flex.Item>
										: null
								}
							</Flex.Vertical>
						</Flex.Item>
						<Flex.Item size='*'>
							<SplitTicketControl
								ref={this.rightLines}
								ticket={this.state.division.Right}
								ticketRole='Right'
								title={this.state.mode === 'manual' ? Res.Web.SPLIT_MANUAL_TICKET_LBL() : Res.Web.SPLIT_AUTO_TICKET_LBL(this.state.currentPart + 1, this.state.totalParts)}
								selectedLine={this.state.selectedSide === 'Right' ? this.state.selectedLine : null}
								onSelect={line => this.setState({ selectedLine: line, selectedSide: 'Right' })}
							/>
						</Flex.Item>
					</Flex.Horizontal>
				</Flex.Item >

				{
					this.state.division.Error ?
						<Flex.Item>
							<Cols>
								<Col className='has-error text-right text-18' width='*'>
									<p><ErrorBlock msg={this.state.division.Error} /></p>
								</Col>
							</Cols>
						</Flex.Item>
						: null
				}

			</Flex.Vertical >
		</Box>;
	}

	createInitialState(props: Props): Nullable<State> {
		return { ...props.initialStatus, selectedLine: props.initialStatus.lines[props.initialStatus.lines.length - 1].line, selectedSide: 'Left' };
	}
}
